import moment from 'moment'
import { RestrictedAccountStatus } from '../enums/RestrictedAccountStatus'
import { AddressContact } from '../interfaces/address-contact'
import {
  ActionLabelValue,
  Address,
  Contact,
  Organization,
  RestrictedProductAccountPolicy,
  User,
} from '../shared.interfaces'
import { RestrictedProductAccountUser } from './restricted-product-account-user'

export type RestrictedProductAccountType =
  | 'Primus'
  | 'Primus XP'
  | 'Everest'
  | 'Everest 29'
  | 'EverestPrimus'
  | 'Falcon'

/* istanbul ignore next */
export class RestrictedProductAccount {
  id: number
  procomAccountNumber: string
  procomSecondaryAccountNumber: string
  productType: RestrictedProductAccountType
  parentRestrictedProductAccountId: number
  productTypeId?: number
  status: number
  organizationId: number
  organization?: Organization
  level?: string
  levelId?: number
  exclusivityTypeId?: number
  exclusivityType?: string
  exclusivityZipCode?: string
  exclusivityCountryCode?: string
  exclusivityTimezoneCode?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCode?: string
  countyCode?: string
  countryCode?: string
  zipCode?: string
  endUser?: User
  name: string
  lockAddressId?: number
  cutKeyAddressId?: number
  masterKeyAddressId?: number
  blankKeyAddressId?: number
  lockContactId?: number
  cutKeyContactId?: number
  masterKeyContactId?: number
  blankKeyContactId?: number
  accessExpiredDate?: string
  accessViewModel?: RestrictedProductAccountPolicy
  options: ActionLabelValue<RestrictedProductAccount>[] = []
  createdBy: string
  createdByEmail: string
  createdOn: Date
  timezoneCode: string
  createdById: number
  restrictedProductAccountUsers: RestrictedProductAccountUser[]
  secondaryRestrictedProductAccounts? = []
  phoneNumber: string
  serialNumberStart?: number
  nextAvailableSerialNumber?: number
  isPaper?: boolean
  isGuid?: boolean
  skipAddressValidation?: boolean

  get accessHasExpired(): boolean {
    if (isNaN(Date.parse(this.accessExpiredDate))) {
      return false
    }
    return moment.utc(Date.parse(this.accessExpiredDate)).toDate() < moment.utc(new Date()).toDate()
  }

  get approved(): boolean {
    return this.status === RestrictedAccountStatus.Approved
  }

  get rejected(): boolean {
    return this.status === RestrictedAccountStatus.Rejected
  }

  get inReview(): boolean {
    return this.status === RestrictedAccountStatus.InReview
  }

  get requested(): boolean {
    return this.status === RestrictedAccountStatus.Requested
  }

  get migrated(): boolean {
    return this.status === RestrictedAccountStatus.Paper
  }

  get inactive(): boolean {
    return this.status === RestrictedAccountStatus.Inactive
  }

  private _accountNumber: string

  get accountNumber(): string {
    return `${this.procomAccountNumber} ${
      this.procomSecondaryAccountNumber ? this.procomSecondaryAccountNumber : this._accountNumber ?? ''
    }`
  }

  set accountNumber(value: string) {
    this._accountNumber = value
  }

  private _organizationName: string

  get organizationName(): string {
    return this.organization ? this.organization.name : this._organizationName ?? ''
  }
  set organizationName(value: string) {
    this._organizationName = value
  }

  organizationAddressesAndContacts(contacts: Contact[], addresses: Address[]): AddressContact[] {
    return [
      {
        label: 'Restricted Locks, Cylinders & Cores *',
        contact: contacts.find((a) => a.id === this.lockContactId),
        address: addresses.find((a) => a.id === this.lockAddressId),
        contactControlName: 'lockContactId',
        addressControlName: 'lockAddressId',
        copyAddressSelected: false,
      },
      {
        label: 'Cut Keys *',
        contact: contacts.find((a) => a.id === this.cutKeyContactId),
        address: addresses.find((a) => a.id === this.cutKeyAddressId),
        contactControlName: 'cutKeyContactId',
        addressControlName: 'cutKeyAddressId',
      },
      {
        label: 'Master Keys *',
        contact: contacts.find((a) => a.id === this.masterKeyContactId),
        address: addresses.find((a) => a.id === this.masterKeyAddressId),
        contactControlName: 'masterKeyContactId',
        addressControlName: 'masterKeyAddressId',
      },
      {
        label: 'Blank Keys *',
        contact: contacts.find((a) => a.id === this.blankKeyContactId),
        address: addresses.find((a) => a.id === this.blankKeyAddressId),
        contactControlName: 'blankKeyContactId',
        addressControlName: 'blankKeyAddressId',
      },
    ]
  }

  areDefaultAddressesSet(): boolean {
    if (
      this.blankKeyAddressId &&
      this.blankKeyContactId &&
      this.cutKeyAddressId &&
      this.cutKeyContactId &&
      this.lockAddressId &&
      this.lockContactId &&
      this.masterKeyAddressId &&
      this.masterKeyContactId
    ) {
      return true
    }

    return false
  }

  private _exclusivityValue: string
  public get exclusivityValue(): string {
    switch (this.exclusivityTypeId) {
      case 5:
      case 6:
        return this.exclusivityZipCode
      case 4:
        return this.exclusivityCountryCode
      case 3:
        return this.exclusivityTimezoneCode
      default:
        return null
    }
  }
  public set exclusivityValue(value: string) {
    switch (this.exclusivityTypeId) {
      case 5:
      case 6:
        this.exclusivityZipCode = value
        break
      case 4:
        this.exclusivityCountryCode = value
        break
      case 3:
        this.exclusivityTimezoneCode = value
        break
      default:
        break
    }
  }
}
